// ============================================
// Admin Dashboard
// ============================================

import React from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, onSnapshot, getDocs, collectionGroup } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Event, EventTherapist, EventAdmin, Session } from '../../types';

// App version - update this when deploying
export const APP_VERSION = '0.2.0';

interface DashboardStats {
  totalEvents: number;
  liveEvents: number;
  totalAdmins: number;
  totalTherapists: number;
  activeSessions: number;
  completedSessions: number;
}

interface AdminInfo {
  uid: string;
  email: string;
  displayName: string;
  eventId: string;
  eventName: string;
}

interface TherapistInfo {
  uid: string;
  displayName: string;
  photoUrl?: string;
  thumbnailUrl?: string;
  eventId: string;
  eventName: string;
  dutyStatus: string;
  uiStatus: string;
}

interface PendingInvite {
  email: string;
  role: 'admin' | 'therapist';
  eventId: string;
  eventName: string;
}

export default function AdminDashboard() {
  const { user, role, impersonateUser, impersonation, stopImpersonation } = useAuth();
  const [impersonating, setImpersonating] = React.useState<string | null>(null);
  const [liveEvents, setLiveEvents] = React.useState<Event[]>([]);
  const [allEvents, setAllEvents] = React.useState<Event[]>([]);
  const [admins, setAdmins] = React.useState<AdminInfo[]>([]);
  const [therapists, setTherapists] = React.useState<TherapistInfo[]>([]);
  const [pendingInvites, setPendingInvites] = React.useState<PendingInvite[]>([]);
  const [stats, setStats] = React.useState<DashboardStats>({
    totalEvents: 0,
    liveEvents: 0,
    totalAdmins: 0,
    totalTherapists: 0,
    activeSessions: 0,
    completedSessions: 0,
  });
  const [loading, setLoading] = React.useState(true);

  // Load events - filter based on role
  React.useEffect(() => {
    if (!user) return;

    // For superadmin, load all events directly
    if (role === 'superadmin') {
      const q = query(collection(db, 'events'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const allEventsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Event[];

        setAllEvents(allEventsData);
        setLiveEvents(allEventsData.filter((e) => e.status === 'live'));
        setStats((prev) => ({
          ...prev,
          totalEvents: allEventsData.length,
          liveEvents: allEventsData.filter((e) => e.status === 'live').length,
        }));
        setLoading(false);
      });

      return unsubscribe;
    }

    // For regular admin, we need to find events where user is an admin
    // First, load all live events (anyone can read these)
    const loadAdminEvents = async () => {
      try {
        const userEvents: Event[] = [];
        const seenEventIds = new Set<string>();

        // Try to get live events first (these are readable by anyone)
        const liveEventsQuery = query(collection(db, 'events'), where('status', '==', 'live'));
        const liveSnapshot = await getDocs(liveEventsQuery);

        // Check which live events this user is admin of
        for (const doc of liveSnapshot.docs) {
          const eventData = { id: doc.id, ...doc.data() } as Event;

          // Check if user is in admins subcollection
          try {
            const adminSnap = await getDocs(collection(db, 'events', doc.id, 'admins'));
            const isAdmin = adminSnap.docs.some((adminDoc) => adminDoc.id === user.uid);
            if (isAdmin || eventData.createdBy === user.uid) {
              userEvents.push(eventData);
              seenEventIds.add(doc.id);
            }
          } catch (err) {
            // Can't read admins, skip
          }
        }

        // Also try draft/closed events by checking admins subcollection
        // We need to query events individually since we can't list all non-live events
        const draftEventsQuery = query(collection(db, 'events'), where('status', '==', 'draft'));
        try {
          const draftSnapshot = await getDocs(draftEventsQuery);
          for (const doc of draftSnapshot.docs) {
            if (seenEventIds.has(doc.id)) continue;
            const eventData = { id: doc.id, ...doc.data() } as Event;
            try {
              const adminSnap = await getDocs(collection(db, 'events', doc.id, 'admins'));
              const isAdmin = adminSnap.docs.some((adminDoc) => adminDoc.id === user.uid);
              if (isAdmin || eventData.createdBy === user.uid) {
                userEvents.push(eventData);
                seenEventIds.add(doc.id);
              }
            } catch (err) {
              // Can't read admins, skip
            }
          }
        } catch (err) {
          // Can't read draft events, that's ok
        }

        setAllEvents(userEvents);
        setLiveEvents(userEvents.filter((e) => e.status === 'live'));
        setStats((prev) => ({
          ...prev,
          totalEvents: userEvents.length,
          liveEvents: userEvents.filter((e) => e.status === 'live').length,
        }));
      } catch (err) {
        console.error('Error loading events:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAdminEvents();
  }, [role, user]);

  // Load admins and therapists for superadmin
  React.useEffect(() => {
    if (role !== 'superadmin' || allEvents.length === 0) return;

    const loadUsersAndStats = async () => {
      const adminsList: AdminInfo[] = [];
      const therapistsList: TherapistInfo[] = [];
      const pendingList: PendingInvite[] = [];
      let activeSessions = 0;
      let completedSessions = 0;

      for (const event of allEvents) {
        // Load admins for each event
        const adminsSnap = await getDocs(collection(db, 'events', event.id, 'admins'));
        const joinedAdminEmails = new Set<string>();
        adminsSnap.docs.forEach((doc) => {
          const data = doc.data() as EventAdmin;
          adminsList.push({
            uid: doc.id,
            email: data.email,
            displayName: data.displayName,
            eventId: event.id,
            eventName: event.name,
          });
          joinedAdminEmails.add(data.email.toLowerCase());
        });

        // Load therapists for each event
        const therapistsSnap = await getDocs(collection(db, 'events', event.id, 'therapists'));
        const joinedTherapistIds = new Set<string>();
        therapistsSnap.docs.forEach((doc) => {
          const data = doc.data() as EventTherapist;
          therapistsList.push({
            uid: doc.id,
            displayName: data.displayName,
            photoUrl: data.photoUrl,
            thumbnailUrl: data.thumbnailUrl,
            eventId: event.id,
            eventName: event.name,
            dutyStatus: data.dutyStatus,
            uiStatus: data.uiStatus,
          });
          joinedTherapistIds.add(doc.id);
        });

        // Load invites to find pending (not yet accepted)
        const invitesSnap = await getDocs(collection(db, 'events', event.id, 'invites'));
        invitesSnap.docs.forEach((doc) => {
          const data = doc.data();
          const allowedEmails = data.allowedEmails as string[] || [];
          const inviteRole = data.role as 'admin' | 'therapist';

          allowedEmails.forEach((email) => {
            const emailLower = email.toLowerCase();
            // For admins, check if email has joined
            if (inviteRole === 'admin' && !joinedAdminEmails.has(emailLower)) {
              pendingList.push({
                email: emailLower,
                role: 'admin',
                eventId: event.id,
                eventName: event.name,
              });
            }
            // For therapists, we can't easily check by email since they use uid
            // We'll show therapist invites as pending if uses < allowedEmails.length
          });

          // For therapist invites, check if there are unused slots
          if (inviteRole === 'therapist') {
            const uses = data.uses as number || 0;
            const unusedCount = allowedEmails.length - uses;
            // Find which emails haven't been used (approximate - show remaining unused emails)
            if (unusedCount > 0) {
              // We can't know exactly which emails haven't been used, so show all non-used
              // This is a best-effort display
              const usedCount = uses;
              const pendingEmails = allowedEmails.slice(usedCount);
              pendingEmails.forEach((email) => {
                pendingList.push({
                  email: email.toLowerCase(),
                  role: 'therapist',
                  eventId: event.id,
                  eventName: event.name,
                });
              });
            }
          }
        });

        // Load session stats
        const sessionsSnap = await getDocs(collection(db, 'events', event.id, 'sessions'));
        sessionsSnap.docs.forEach((doc) => {
          const data = doc.data() as Session;
          if (data.status === 'active') activeSessions++;
          if (data.status === 'completed') completedSessions++;
        });
      }

      setAdmins(adminsList);
      setTherapists(therapistsList);
      setPendingInvites(pendingList);
      setStats((prev) => ({
        ...prev,
        totalAdmins: adminsList.length,
        totalTherapists: therapistsList.length,
        activeSessions,
        completedSessions,
      }));
    };

    loadUsersAndStats();
  }, [role, allEvents]);

  const getStatusColor = (uiStatus: string) => {
    switch (uiStatus) {
      case 'green':
        return 'var(--color-green)';
      case 'orange':
        return 'var(--color-orange)';
      case 'red':
        return 'var(--color-red)';
      case 'blue':
        return 'var(--color-blue)';
      default:
        return 'var(--color-grey)';
    }
  };

  const handleImpersonate = async (targetUid: string, targetName: string) => {
    if (!window.confirm(`Login as ${targetName}? You will see the app as they see it.`)) {
      return;
    }

    setImpersonating(targetUid);
    try {
      await impersonateUser(targetUid);
      // After impersonation, redirect to therapist home
      window.location.href = '/t';
    } catch (err: any) {
      alert(err.message || 'Failed to impersonate user');
      setImpersonating(null);
    }
  };

  const handleStopImpersonation = async () => {
    await stopImpersonation();
    window.location.href = '/login';
  };

  return (
    <div className="admin-dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
            v{APP_VERSION}
          </span>
          <Link to="/admin/events/new" className="btn btn-primary">
            + New Event
          </Link>
        </div>
      </div>

      {/* Stats Overview - SuperAdmin only */}
      {role === 'superadmin' && (
        <section className="dashboard-section" style={{ marginBottom: '2.5rem' }}>
          <h2>Overview</h2>
          <div className="stats-bar">
            <div className="stat">
              <span className="stat-value">{stats.totalEvents}</span>
              <span className="stat-label">Total Events</span>
            </div>
            <div className="stat" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
              <span className="stat-value">{stats.liveEvents}</span>
              <span className="stat-label">Live Events</span>
            </div>
            <div className="stat">
              <span className="stat-value">{stats.totalAdmins}</span>
              <span className="stat-label">Event Admins</span>
            </div>
            <div className="stat">
              <span className="stat-value">{stats.totalTherapists}</span>
              <span className="stat-label">Therapists</span>
            </div>
            <div className="stat" style={{ background: 'rgba(239, 68, 68, 0.2)' }}>
              <span className="stat-value">{stats.activeSessions}</span>
              <span className="stat-label">Active Sessions</span>
            </div>
            <div className="stat">
              <span className="stat-value">{stats.completedSessions}</span>
              <span className="stat-label">Completed</span>
            </div>
          </div>
        </section>
      )}

      <section className="dashboard-section" style={{ marginBottom: '2.5rem' }}>
        <h2>Live Events</h2>
        {loading ? (
          <div className="loading">Loading...</div>
        ) : liveEvents.length === 0 ? (
          <div className="empty-state">
            <p>No live events</p>
            <Link to="/admin/events/new">Create your first event</Link>
          </div>
        ) : (
          <div className="events-grid">
            {liveEvents.map((event) => (
              <Link
                key={event.id}
                to={`/admin/events/${event.id}`}
                className="event-card live"
                style={{
                  padding: '1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className="event-status" style={{ margin: 0 }}>LIVE</div>
                </div>
                <h3 style={{ margin: 0, fontSize: '1.25rem' }}>{event.name}</h3>
                <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>{event.venueName}</p>
                {(event.city || event.country) && (
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>📍</span> {[event.city, event.country].filter(Boolean).join(', ')}
                  </p>
                )}
                {(event.startDate || event.endDate) && (
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>📅</span> {event.startDate} {event.endDate && `- ${event.endDate}`}
                  </p>
                )}
                <div className="event-meta" style={{ marginTop: '0.5rem', display: 'flex', gap: '1.5rem' }}>
                  <span>{event.tablesCount} tables</span>
                  <span>{event.currency} {event.pricePerMinute}/min</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Admins List - SuperAdmin only */}
      {role === 'superadmin' && (admins.length > 0 || pendingInvites.filter(p => p.role === 'admin').length > 0) && (
        <section className="dashboard-section" style={{ marginBottom: '2.5rem' }}>
          <h2>Event Admins ({admins.length} accepted{pendingInvites.filter(p => p.role === 'admin').length > 0 && `, ${pendingInvites.filter(p => p.role === 'admin').length} pending`})</h2>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Event</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin, idx) => (
                  <tr key={`${admin.uid}-${admin.eventId}-${idx}`}>
                    <td>{admin.displayName}</td>
                    <td>{admin.email}</td>
                    <td>
                      <Link to={`/admin/events/${admin.eventId}`} className="btn btn-sm btn-secondary">
                        {admin.eventName}
                      </Link>
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: 'rgba(34, 197, 94, 0.2)',
                        color: 'var(--color-green)',
                      }}>Accepted</span>
                    </td>
                  </tr>
                ))}
                {pendingInvites.filter(p => p.role === 'admin').map((invite, idx) => (
                  <tr key={`pending-admin-${invite.email}-${invite.eventId}-${idx}`} style={{ opacity: 0.8 }}>
                    <td style={{ color: 'var(--color-text-secondary)' }}>-</td>
                    <td>{invite.email}</td>
                    <td>
                      <Link to={`/admin/events/${invite.eventId}`} className="btn btn-sm btn-secondary">
                        {invite.eventName}
                      </Link>
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: 'rgba(249, 115, 22, 0.2)',
                        color: 'var(--color-orange)',
                      }}>Pending</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Therapists List - SuperAdmin only */}
      {role === 'superadmin' && (therapists.length > 0 || pendingInvites.filter(p => p.role === 'therapist').length > 0) && (
        <section className="dashboard-section" style={{ marginBottom: '2.5rem' }}>
          <h2>Therapists ({therapists.length} accepted{pendingInvites.filter(p => p.role === 'therapist').length > 0 && `, ${pendingInvites.filter(p => p.role === 'therapist').length} pending`})</h2>
          <div className="therapist-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
            {therapists.map((therapist, idx) => (
              <div
                key={`${therapist.uid}-${therapist.eventId}-${idx}`}
                className="therapist-card"
                style={{ padding: '1rem', position: 'relative' }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  border: `3px solid ${getStatusColor(therapist.uiStatus)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {(therapist.thumbnailUrl || therapist.photoUrl) ? (
                    <img
                      src={therapist.thumbnailUrl || therapist.photoUrl}
                      alt=""
                      style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: 'var(--color-bg-tertiary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                      }}
                    >
                      {therapist.displayName.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="therapist-name" style={{ marginTop: '0.5rem', fontWeight: '600' }}>
                  {therapist.displayName}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>
                  {therapist.eventName}
                </div>
                <div style={{ fontSize: '0.7rem', color: getStatusColor(therapist.uiStatus), textTransform: 'capitalize' }}>
                  {therapist.dutyStatus.replace('_', ' ')}
                </div>
                <button
                  onClick={() => handleImpersonate(therapist.uid, therapist.displayName)}
                  disabled={impersonating === therapist.uid}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.65rem',
                    backgroundColor: 'var(--color-bg-tertiary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {impersonating === therapist.uid ? 'Loading...' : 'Login As'}
                </button>
              </div>
            ))}
            {/* Pending therapist invites */}
            {pendingInvites.filter(p => p.role === 'therapist').map((invite, idx) => (
              <div
                key={`pending-therapist-${invite.email}-${invite.eventId}-${idx}`}
                className="therapist-card"
                style={{ padding: '1rem', position: 'relative', opacity: 0.8 }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  border: '3px dashed var(--color-orange)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <div
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: 'var(--color-bg-tertiary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    ?
                  </div>
                </div>
                <div className="therapist-name" style={{ marginTop: '0.5rem', fontWeight: '600', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                  {invite.email}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>
                  {invite.eventName}
                </div>
                <span style={{
                  display: 'inline-block',
                  marginTop: '0.25rem',
                  padding: '0.15rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  backgroundColor: 'rgba(249, 115, 22, 0.2)',
                  color: 'var(--color-orange)',
                }}>Pending</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
